import { useEffect, useRef, useState } from 'react';
import { VideoLoader } from '../utils/videoUtils';

interface UseVideoPreloaderOptions {
  preloadNext?: boolean;
  preloadPrevious?: boolean;
  maxCacheSize?: number;
}

export const useVideoPreloader = (
  videos: Array<{ videoUrl: string; thumbnailUrl: string }>,
  currentIndex: number,
  options: UseVideoPreloaderOptions = {}
) => {
  const { preloadNext = true, preloadPrevious = true, maxCacheSize = 3 } = options;
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>({});
  const [errorStates, setErrorStates] = useState<Record<number, string>>({});
  const preloadQueue = useRef<Set<number>>(new Set());

  const preloadVideo = async (index: number) => {
    if (index < 0 || index >= videos.length || preloadQueue.current.has(index)) {
      return;
    }

    const video = videos[index];
    if (!video) return;

    preloadQueue.current.add(index);
    setLoadingStates(prev => ({ ...prev, [index]: true }));
    setErrorStates(prev => ({ ...prev, [index]: '' }));

    try {
      await VideoLoader.preloadVideo(video.videoUrl, { preload: 'metadata' });
      setLoadingStates(prev => ({ ...prev, [index]: false }));
    } catch (error) {
      console.error(`Failed to preload video ${index}:`, error);
      setErrorStates(prev => ({ 
        ...prev, 
        [index]: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      }));
      setLoadingStates(prev => ({ ...prev, [index]: false }));
    } finally {
      preloadQueue.current.delete(index);
    }
  };

  // Preload adjacent videos when current index changes
  useEffect(() => {
    const preloadAdjacent = async () => {
      const toPreload: number[] = [];
      
      // Always preload current video
      toPreload.push(currentIndex);
      
      // Preload next video
      if (preloadNext) {
        const nextIndex = currentIndex === videos.length - 1 ? 0 : currentIndex + 1;
        toPreload.push(nextIndex);
      }
      
      // Preload previous video
      if (preloadPrevious) {
        const prevIndex = currentIndex === 0 ? videos.length - 1 : currentIndex - 1;
        toPreload.push(prevIndex);
      }
      
      // Limit cache size by clearing old entries
      const currentLoadedCount = Object.keys(loadingStates).length;
      if (currentLoadedCount > maxCacheSize) {
        // Clear cache for videos that are far from current index
        const indicesToClear = Object.keys(loadingStates)
          .map(Number)
          .filter(index => {
            const distance = Math.min(
              Math.abs(index - currentIndex),
              Math.abs(index - currentIndex + videos.length),
              Math.abs(index - currentIndex - videos.length)
            );
            return distance > 2; // Keep videos within 2 positions
          });
        
        indicesToClear.forEach(index => {
          setLoadingStates(prev => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
          });
          setErrorStates(prev => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
          });
        });
      }
      
      // Preload videos
      for (const index of toPreload) {
        preloadVideo(index);
      }
    };

    preloadAdjacent();
  }, [currentIndex, videos.length, preloadNext, preloadPrevious, maxCacheSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      preloadQueue.current.clear();
    };
  }, []);

  return {
    isLoading: (index: number) => loadingStates[index] || false,
    hasError: (index: number) => !!errorStates[index],
    getError: (index: number) => errorStates[index] || '',
    preloadVideo,
    clearCache: () => {
      VideoLoader.clearCache();
      setLoadingStates({});
      setErrorStates({});
      preloadQueue.current.clear();
    }
  };
};